import { useCallback, useState } from "react";
import * as apiClient from "@/services/api-client";
import { downloadBlob } from "@/utils/blob-download";

type TranslationFunction = (
  key: string,
  options?: Record<string, unknown>,
) => string;

type UseAuditActionsOptions = {
  total: number;
  /**
   * True while history filters/search are narrowing the listing. Bulk
   * delete removes every deletable audit server-side (it isn't scoped by
   * filters), so while the view is filtered `total` understates the actual
   * deletion scope — hidden audits would be permanently deleted too.
   * Callers must block the action until filters are cleared (F077).
   */
  hasActiveFilters?: boolean;
  onSuccess?: (message: string) => void;
  /** Transient action failures (delete/download/export). Callers route this to
   * a Toast; the actionError state is still exposed for backwards-compat. */
  onError?: (message: string) => void;
  t?: TranslationFunction;
  onRefresh: (showLoadingSpinner?: boolean, pageOverride?: number) => Promise<void>;
  onPageReset?: () => void;
};

export function useAuditActions({
  total,
  hasActiveFilters,
  onSuccess,
  onError,
  t,
  onRefresh,
  onPageReset,
}: UseAuditActionsOptions) {
  const [deletingAuditId, setDeletingAuditId] = useState<string | null>(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set the (legacy) actionError state and forward to the onError callback so
  // callers can surface it as a Toast.
  const reportError = useCallback(
    (message: string) => {
      setError(message);
      onError?.(message);
    },
    [onError],
  );

  const handleDownload = useCallback(
    async (auditId: string, fileName: string) => {
      try {
        const blob = await apiClient.downloadAudit(auditId);
        downloadBlob(blob, fileName);
      } catch (err) {
        if (err instanceof apiClient.ApiError) {
          reportError(err.message);
        } else {
          reportError(
            t?.("errors.downloadFailed") ?? "Failed to download file",
          );
        }
      }
    },
    [reportError, t],
  );

  const handleExportJson = useCallback(
    async (auditId: string, documentName: string) => {
      try {
        const blob = await apiClient.exportAuditJson(auditId);
        const baseName = documentName.replace(/\.[^.]+$/, "");
        downloadBlob(blob, `${baseName}-audit.json`);
      } catch (err) {
        if (err instanceof apiClient.ApiError) {
          reportError(err.message);
        } else {
          reportError(
            t?.("errors.exportFailed") ?? "Failed to export audit",
          );
        }
      }
    },
    [reportError, t],
  );

  const handleDelete = useCallback(
    async (auditId: string) => {
      if (
        !confirm(
          t?.("deleteConfirm.message") ??
            "Are you sure you want to delete this audit? This action cannot be undone.",
        )
      ) {
        return;
      }

      try {
        setDeletingAuditId(auditId);
        await apiClient.deleteAudit(auditId);
        await onRefresh(false);
        onSuccess?.(
          t?.("deleteConfirm.success") ?? "Audit deleted successfully",
        );
      } catch (err) {
        if (err instanceof apiClient.ApiError) {
          reportError(err.message);
        } else {
          reportError(t?.("errors.deleteFailed") ?? "Failed to delete audit");
        }
      } finally {
        setDeletingAuditId(null);
      }
    },
    [onRefresh, onSuccess, reportError, t],
  );

  const handleDeleteAll = useCallback(async () => {
    if (total === 0 || deletingAll || hasActiveFilters) {
      return;
    }

    // Always route through the plural key so i18next picks the language's
    // singular form for total === 1. A dedicated "confirmMessageOne" key does
    // not exist, so calling it returned the raw key string to the user.
    const confirmMessage =
      t?.("deleteAll.confirmMessage", { count: total }) ??
      (total === 1
        ? "Delete 1 audit? This will permanently remove your audit history and documents. This action cannot be undone."
        : `Delete all ${total} audits? This will permanently remove your audit history and documents. This action cannot be undone.`);
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      setDeletingAll(true);
      const result = await apiClient.deleteAllAudits();
      const deletedCount = result.deletedCount ?? total;
      const failedDeletes = result.failedFileDeletes ?? 0;
      onPageReset?.();
      await onRefresh(false, 1);

      const warning = failedDeletes > 0
        ? ` ${
          t?.("deleteAll.fileDeleteWarning", { count: failedDeletes }) ??
            `${failedDeletes} file(s) could not be removed from storage.`
        }`
        : "";
      const successMsg = t?.("deleteAll.successCount", { count: deletedCount }) ??
        `Deleted ${deletedCount} audit(s).`;
      onSuccess?.(`${successMsg}${warning}`);
    } catch (err) {
      if (err instanceof apiClient.ApiError) {
        reportError(err.message);
      } else {
        reportError(
          t?.("errors.deleteAllFailed") ?? "Failed to delete audits",
        );
      }
    } finally {
      setDeletingAll(false);
    }
  }, [deletingAll, hasActiveFilters, onPageReset, onRefresh, onSuccess, reportError, t, total]);

  return {
    deletingAuditId,
    deletingAll,
    // False while history filters/search are active: bulk delete is
    // unfiltered, so callers should disable the trigger until filters are
    // cleared (F077).
    canDeleteAll: !hasActiveFilters,
    actionError: error,
    handleDownload,
    handleExportJson,
    handleDelete,
    handleDeleteAll,
  };
}
